/**
 * R5 — RP67 (MOA-A4) : fiche lisible d'un avenant, telle que le client la lit :
 * quoi, pourquoi, montant en plus ou en moins (HT et TTC), nouveau total du marché,
 * effet sur le délai ; puis la décision du client et l'avis de l'AMO.
 * Partagée par l'onglet Marchés (AvenantsMarche) et l'écran du client (AvenantsADecider).
 */
import { STATUT_AVENANT } from '../../lib/libelles';

export const CLE_STATUT_AVENANT = { PROPOSE: 'propose', ACCEPTE: 'accepte', REFUSE: 'refuse' };

export function libelleStatutAvenant(av) {
  if (av?.statut === 'VALIDE') return 'Validé (ancien circuit)'; // validé par l'architecte seul, avant R5
  return STATUT_AVENANT[CLE_STATUT_AVENANT[av?.statut]] || av?.statut_libelle || 'Brouillon';
}

const fmtEur = (n) => (n === null || n === undefined ? '—'
  : `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return ''; }
};
const signe = (n) => (n > 0 ? '+' : '');

const COULEUR_STATUT = {
  PROPOSE: 'bg-[#FFF3E0] text-[#C4773B]',
  ACCEPTE: 'bg-[#E8F5E9] text-[#2D6A4F]',
  REFUSE: 'bg-[#FEF2F2] text-[#C53030]',
};

export default function AvenantFiche({ avenant, publicClient = false, children }) {
  const f = avenant?.fiche;
  const statut = avenant?.statut;
  return (
    <div className="border border-[#E0E0DB] bg-white" data-testid={`avenant-fiche-${avenant.id}`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-[#FAFAF8] border-b border-[#E0E0DB]">
        <span className="font-bold text-[#2C2C2C]">Avenant n°{avenant.numero}</span>
        <span className="text-sm text-[#2C2C2C] flex-1 min-w-0 truncate">{f?.objet || avenant.label}</span>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${COULEUR_STATUT[statut] || 'bg-[#F5F5F3] text-[#6B6B6B]'}`}
          data-testid={`avenant-statut-${avenant.id}`}>
          {libelleStatutAvenant(avenant)}
        </span>
      </div>
      {f ? (
        <div className="p-4 grid gap-3 sm:grid-cols-2 text-sm">
          <div className="sm:col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] font-bold">
              {publicClient ? 'Pourquoi cette modification' : 'Pourquoi (motif)'}
            </p>
            <p className="text-[#2C2C2C] whitespace-pre-line">{f.motif || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] font-bold">
              {f.sens === 'moins' ? 'Travaux en moins' : 'Travaux en plus'}
            </p>
            <p className={`text-lg font-bold ${f.sens === 'moins' ? 'text-[#2D6A4F]' : 'text-[#C75B39]'}`}>
              {signe(f.montant_ht)}{fmtEur(f.montant_ht)} HT
            </p>
            <p className="text-xs text-[#6B6B6B]">soit {signe(f.montant_ttc)}{fmtEur(f.montant_ttc)} TTC</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] font-bold">Effet sur le délai</p>
            <p className="text-lg font-bold text-[#2C2C2C]" data-testid={`avenant-delai-${avenant.id}`}>
              {f.effet_delai_jours
                ? `${signe(f.effet_delai_jours)}${f.effet_delai_jours} jour${Math.abs(f.effet_delai_jours) > 1 ? 's' : ''}`
                : 'Aucun'}
            </p>
          </div>
          {(f.lots || []).length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A] font-bold mb-1">
                {publicClient ? "Contrat de l'entreprise concerné" : 'Marché concerné'}
              </p>
              {(f.lots || []).map((l) => (
                <div key={l.lot_marche_id} className="flex flex-wrap justify-between gap-2 text-xs border-t border-[#F0F0EE] py-1">
                  <span>{l.code} {l.nom}{l.entreprise_nom ? ` — ${l.entreprise_nom}` : ''}</span>
                  {/* A5 / P4#13 — montants masqués au client : pas de total du marché */}
                  {l.marche_total_apres_ht != null ? (
                    <span>
                      {fmtEur(l.marche_total_avant_ht)} → <strong>{fmtEur(l.marche_total_apres_ht)}</strong> HT
                    </span>
                  ) : (
                    <span>{fmtEur(l.montant_ht)} HT</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {(f.postes || []).length > 0 && (
            <details className="sm:col-span-2 text-xs">
              <summary className="cursor-pointer text-[#1E3A5F]">Détail des travaux ({f.postes.length})</summary>
              <ul className="mt-1 space-y-0.5">
                {f.postes.map((p, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span>{p.designation} — {p.quantite} {p.unite}</span>
                    <span>{fmtEur(p.montant_ht)} HT</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {avenant.decision && (
            <div className="sm:col-span-2 text-xs text-[#2C2C2C] border-t border-[#E0E0DB] pt-2" data-testid={`avenant-decision-${avenant.id}`}>
              {avenant.decision.decision === 'ACCEPTE' ? 'Accepté' : 'Refusé'} par {avenant.decision.par_nom || 'le client'}
              {' '}le {fmtDate(avenant.decision.le)} (signature électronique par code envoyé par SMS ou par e-mail)
              {avenant.decision.commentaire && <span className="block italic text-[#6B6B6B]">« {avenant.decision.commentaire} »</span>}
            </div>
          )}
          {avenant.avis_amo && (
            <div className="sm:col-span-2 text-xs border-l-4 border-[#7B5EA7] bg-[#F5F0FB] px-3 py-2" data-testid={`avenant-avis-amo-${avenant.id}`}>
              Avis de l'AMO : <strong>{avenant.avis_amo.avis_libelle}</strong>
              {avenant.avis_amo.par_nom && ` (${avenant.avis_amo.par_nom})`}
              {avenant.avis_amo.commentaire && <span className="block italic">« {avenant.avis_amo.commentaire} »</span>}
            </div>
          )}
        </div>
      ) : (
        <p className="p-4 text-xs text-[#9A9A9A]">
          Brouillon : pas encore soumis au client ({fmtEur(avenant.montant_ht)} HT).
        </p>
      )}
      {children}
    </div>
  );
}
